import axios, { AxiosResponse } from 'axios';
import { AuthResponse, ApiAuthResponse, LoginCredentials, User, ApiResponse } from '../types';
import { queueAction } from '../utils/offlineDb';
import { requestBackgroundSync } from '../utils/syncQueue';
import { getApiWfAuthToken } from './oauthService';

// Use /api as base URL since nginx will proxy to correct services
const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const AUTH_BYPASS_PATHS = [
  '/auth/login',
  '/auth/logout',
  '/auth/refresh',
  '/auth/oauth/validate',
  '/auth/oauth/logout',
];

let refreshPromise: Promise<string | null> | null = null;

const clearLocalAuthState = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('currentUserId');
};

// Inner-JWT failure: only the inner auth state is cleared. The WayFong OAuth
// session is intentionally left alone — it's an independent domain-gate layer
// and dies on its own schedule via useOAuth's periodic check.
const redirectToLogin = () => {
  clearLocalAuthState();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const refreshAccessToken = async (): Promise<string | null> => {
  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) {
    return null;
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const response: AxiosResponse<ApiAuthResponse> = await api.post('/auth/refresh', {
          refreshToken: storedRefreshToken,
        });
        const authResponse = response.data.data;
        localStorage.setItem('authToken', authResponse.tokens.accessToken);
        localStorage.setItem('refreshToken', authResponse.tokens.refreshToken);
        localStorage.setItem('currentUserId', authResponse.user.id);
        return authResponse.tokens.accessToken;
      } catch {
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }

  return refreshPromise;
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // WCO-2914: also attach the wf-auth token so the api-gateway can re-check HTE access
  // server-side. Harmless when the gateway gate is off (it ignores the header).
  const wfAuthToken = getApiWfAuthToken();
  if (wfAuthToken) {
    config.headers['x-wayfong-token'] = `Bearer ${wfAuthToken}`;
  }
  return config;
});

// Handle token refresh and 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const originalRequest = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
      const requestUrl = originalRequest?.url || '';
      const isBypassedPath = AUTH_BYPASS_PATHS.some((path) => requestUrl.includes(path));
      const shouldBypassRefresh = !originalRequest || isBypassedPath;

      if (!shouldBypassRefresh && !originalRequest._retry) {
        originalRequest._retry = true;
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }
      }

      if (!isBypassedPath) {
        redirectToLogin();
      }
    }
    return Promise.reject(error);
  }
);

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response: AxiosResponse<ApiAuthResponse> = await api.post('/auth/login', credentials);
    return response.data.data;
  },

  async logout(): Promise<void> {
    await api.post('/auth/logout');
    clearLocalAuthState();
  },

  async getCurrentUser(): Promise<User> {
    const response: AxiosResponse<{status: string; data: {user: User}}> = await api.get('/auth/me');
    return response.data.data.user;
  },

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refreshToken');
    const response: AxiosResponse<ApiAuthResponse> = await api.post('/auth/refresh', {
      refreshToken,
    });
    return response.data.data;
  },

  // Verify supervisor credentials without logging them in
  // Used for yield variance approval
  async verifySupervisor(
    username: string,
    password: string
  ): Promise<{
    valid: boolean;
    userId?: string;
    fullName?: string;
    error?: string;
  }> {
    const response = await api.post('/auth/verify-supervisor', {
      username,
      password,
    });
    return response.data;
  },

  // ICS User Management (Admin only)
  async getIcsUsers(): Promise<any[]> {
    const response = await api.get('/auth/ics-users');
    return response.data.data;
  },

  async getIcsAvailableUsers(): Promise<any[]> {
    const response = await api.get('/auth/ics-users', { params: { source: 'ics-available' } });
    return response.data.data;
  },

  async enableUser(userId: string, role: string): Promise<any> {
    const response = await api.post('/auth/enable-user', { userId, role });
    return response.data;
  },

  async disableUser(userId: string, disabled?: boolean): Promise<any> {
    const response = await api.post('/auth/disable-user', { userId, disabled });
    return response.data;
  },

  async deleteProductionUser(userId: string): Promise<any> {
    const response = await api.post('/auth/delete-user', { userId });
    return response.data;
  },

  async resetTempPassword(userId: string): Promise<any> {
    const response = await api.post('/auth/reset-temp-password', { userId });
    return response.data;
  },

  // Force password change (first login)
  async forceChangePassword(newPassword: string, confirmPassword: string): Promise<any> {
    const response = await api.post('/auth/force-change-password', { newPassword, confirmPassword });
    return response.data;
  },

  // Mark training walkthrough as completed
  async completeTraining(): Promise<any> {
    const response = await api.post('/auth/complete-training');
    return response.data;
  },

  // Verify supervisor by badge scan (employee ID card)
  // Used for yield variance approval when supervisor scans their badge
  async verifySupervisorBadge(badge: string): Promise<{
    valid: boolean;
    userId?: string;
    employeeNumber?: string;
    fullName?: string;
    error?: string;
  }> {
    const response = await api.post('/auth/verify-supervisor-badge', { badge });
    return response.data;
  },
};

export default api;
